import {Injectable, NgZone} from '@angular/core';
import {defer, EMPTY, from, fromEvent, merge, Observable, of, ReplaySubject, throwError, timer} from 'rxjs';
import {catchError, filter, finalize, map, mergeMap, repeat, switchMap, take, tap, timeout} from 'rxjs/operators';
import {LoggerService} from '../logger.service';
import {environment} from '../../../environments/environment';

declare const AwsWafIntegration: any;

enum AwsWafIntegrationState {
  UNKNOWN = 'UNKNOWN',
  AVAILABLE = 'AVAILABLE',
  UNAVAILABLE = 'UNAVAILABLE'
}

type AwsWafIntegrationReadiness =
  | { integrationLoaded: true }
  | { integrationLoaded: false, failureReason: string };

@Injectable({
  providedIn: 'root'
})
export class AwsWafTokenService {

  private static readonly challengeScriptMarker = '/challenge';
  private static readonly integrationPollInterval = 100;
  private static readonly challengeScriptFallbackDelay = 2000;
  private static readonly integrationReadinessTimeout = 10000;
  private static readonly tokenRequestTimeout = 10000;
  private static readonly initialRecoveryProbeDelay = 2000;
  private static readonly maxRecoveryProbeDelay = 60000;

  private state: AwsWafIntegrationState = AwsWafIntegrationState.UNKNOWN;
  private readinessProbe: Observable<boolean> | null = null;
  private pendingToken: Observable<string | null> | null = null;
  private pendingTokenForcesRefresh = false;
  private recoveryProbing = false;
  private recoveryProbeDelay: number = AwsWafTokenService.initialRecoveryProbeDelay;

  constructor(private loggerService: LoggerService,
              private ngZone: NgZone) {
    this.getToken(false).subscribe();
  }

  public getToken(forceRefreshToken: boolean): Observable<string | null> {
    return this.awaitIntegration().pipe(
      switchMap(integrationAvailable => integrationAvailable ? this.requestToken(forceRefreshToken) : of(null))
    );
  }

  public isIntegrationAvailable(): boolean {
    return this.state !== AwsWafIntegrationState.UNAVAILABLE;
  }

  private awaitIntegration(): Observable<boolean> {
    if (this.state === AwsWafIntegrationState.AVAILABLE) {
      return of(true);
    }
    if (this.state === AwsWafIntegrationState.UNAVAILABLE) {
      return of(false);
    }
    return this.readinessProbe ?? this.startReadinessProbe();
  }

  private startReadinessProbe(): Observable<boolean> {
    const readiness = new ReplaySubject<boolean>(1);
    const readinessProbe = readiness.asObservable();
    this.readinessProbe = readinessProbe;
    this.ngZone.runOutsideAngular(() =>
      this.observeIntegrationReadiness().pipe(
        tap(readinessResult => readinessResult.integrationLoaded === true
          ? this.markAvailable()
          : this.markUnavailable(readinessResult.failureReason)),
        map(readinessResult => readinessResult.integrationLoaded),
        finalize(() => this.discardReadinessProbe(readinessProbe))
      ).subscribe(readiness)
    );
    return readinessProbe;
  }

  private observeIntegrationReadiness(): Observable<AwsWafIntegrationReadiness> {
    return merge(
      this.observeIntegrationLoaded(),
      this.observeChallengeScriptFallback()
    ).pipe(
      timeout({
        each: AwsWafTokenService.integrationReadinessTimeout,
        with: () => of<AwsWafIntegrationReadiness>({
          integrationLoaded: false,
          failureReason: `AwsWafIntegration was not loaded within ${AwsWafTokenService.integrationReadinessTimeout}ms`
        })
      }),
      take(1)
    );
  }

  private observeIntegrationLoaded(): Observable<AwsWafIntegrationReadiness> {
    return merge(
      of(null),
      this.observeChallengeScript().pipe(mergeMap(challengeScript => fromEvent(challengeScript, 'load'))),
      timer(AwsWafTokenService.integrationPollInterval, AwsWafTokenService.integrationPollInterval)
    ).pipe(
      filter(() => this.isIntegrationLoaded()),
      take(1),
      map(() => ({integrationLoaded: true}) as AwsWafIntegrationReadiness)
    );
  }

  private observeChallengeScriptFallback(): Observable<AwsWafIntegrationReadiness> {
    return merge(
      this.observeChallengeScript().pipe(mergeMap(challengeScript => fromEvent(challengeScript, 'error'))),
      timer(AwsWafTokenService.challengeScriptFallbackDelay).pipe(filter(() => !this.queryChallengeScript()))
    ).pipe(
      take(1),
      switchMap(() => this.loadConfiguredChallengeScript())
    );
  }

  private loadConfiguredChallengeScript(): Observable<AwsWafIntegrationReadiness> {
    if (!environment.AWS_WAF_CHALLENGE_SCRIPT_URL) {
      this.loggerService.ddWarn('No AWS WAF challenge script url is configured, AwsWafIntegration cannot be loaded as a fallback');
      return EMPTY;
    }
    this.loggerService.ddWarn(`jsapi.js did not provide a working AWS WAF challenge script, loading ${environment.AWS_WAF_CHALLENGE_SCRIPT_URL} instead`);
    return fromEvent(this.appendConfiguredChallengeScript(), 'error').pipe(
      map(() => ({
        integrationLoaded: false,
        failureReason: `${environment.AWS_WAF_CHALLENGE_SCRIPT_URL} failed to load`
      }) as AwsWafIntegrationReadiness)
    );
  }

  private appendConfiguredChallengeScript(): HTMLScriptElement {
    const challengeScript = document.createElement('script');
    challengeScript.src = environment.AWS_WAF_CHALLENGE_SCRIPT_URL;
    challengeScript.async = true;
    document.head.appendChild(challengeScript);
    return challengeScript;
  }

  private observeChallengeScript(): Observable<HTMLScriptElement> {
    return merge(
      of(null),
      timer(AwsWafTokenService.integrationPollInterval, AwsWafTokenService.integrationPollInterval)
    ).pipe(
      map(() => this.queryChallengeScript()),
      filter((challengeScript): challengeScript is HTMLScriptElement => !!challengeScript),
      take(1)
    );
  }

  private queryChallengeScript(): HTMLScriptElement | null {
    return Array.from(document.querySelectorAll('script'))
      .find(script => script.src.includes(AwsWafTokenService.challengeScriptMarker)) ?? null;
  }

  private requestToken(forceRefreshToken: boolean): Observable<string | null> {
    if (this.pendingToken && (this.pendingTokenForcesRefresh || !forceRefreshToken)) {
      return this.pendingToken;
    }
    const token = new ReplaySubject<string | null>(1);
    const pendingToken = token.asObservable();
    this.pendingToken = pendingToken;
    this.pendingTokenForcesRefresh = forceRefreshToken;
    this.ngZone.runOutsideAngular(() =>
      defer(() => this.callIntegrationToken(forceRefreshToken)).pipe(
        tap(() => this.markAvailable()),
        catchError(error => {
          this.markUnavailable(`AwsWafIntegration failed to issue a token: ${error?.message || error}`);
          return of(null);
        }),
        finalize(() => this.discardPendingToken(pendingToken))
      ).subscribe(token)
    );
    return pendingToken;
  }

  private callIntegrationToken(forceRefreshToken: boolean): Observable<string> {
    const issueToken = forceRefreshToken ? AwsWafIntegration.forceRefreshToken() : AwsWafIntegration.getToken();
    return from(issueToken as Promise<string>).pipe(
      timeout(AwsWafTokenService.tokenRequestTimeout),
      switchMap(token => {
        if (token) {
          return of(token);
        }
        if (forceRefreshToken) {
          return throwError(() => new Error('AwsWafIntegration returned an empty token'));
        }
        return this.callIntegrationToken(true);
      })
    );
  }

  private discardReadinessProbe(readinessProbe: Observable<boolean>): void {
    if (this.readinessProbe === readinessProbe) {
      this.readinessProbe = null;
    }
  }

  private discardPendingToken(pendingToken: Observable<string | null>): void {
    if (this.pendingToken === pendingToken) {
      this.pendingToken = null;
    }
  }

  private markAvailable(): void {
    if (this.state === AwsWafIntegrationState.UNAVAILABLE) {
      this.loggerService.ddInfo('AwsWafIntegration recovered, API calls are made with the x-aws-waf-token header again');
    }
    this.state = AwsWafIntegrationState.AVAILABLE;
    this.recoveryProbeDelay = AwsWafTokenService.initialRecoveryProbeDelay;
  }

  private markUnavailable(reason: string): void {
    if (this.state !== AwsWafIntegrationState.UNAVAILABLE) {
      this.state = AwsWafIntegrationState.UNAVAILABLE;
      this.loggerService.ddError(`AwsWafIntegration is unavailable, API calls are made without the x-aws-waf-token header until it recovers: ${reason}`);
    }
    this.startRecoveryProbing();
  }

  private startRecoveryProbing(): void {
    if (this.recoveryProbing) {
      return;
    }
    this.recoveryProbing = true;
    this.ngZone.runOutsideAngular(() =>
      defer(() => timer(this.nextRecoveryProbeDelay())).pipe(
        switchMap(() => this.probeIntegrationHealth()),
        filter(integrationHealthy => integrationHealthy),
        repeat(),
        take(1),
        finalize(() => this.recoveryProbing = false)
      ).subscribe()
    );
  }

  private probeIntegrationHealth(): Observable<boolean> {
    if (!this.isIntegrationLoaded()) {
      return of(false);
    }
    return this.requestToken(false).pipe(map(token => !!token));
  }

  private nextRecoveryProbeDelay(): number {
    const recoveryProbeDelay = this.recoveryProbeDelay;
    this.recoveryProbeDelay = Math.min(recoveryProbeDelay * 2, AwsWafTokenService.maxRecoveryProbeDelay);
    return recoveryProbeDelay;
  }

  private isIntegrationLoaded(): boolean {
    const integration = (window as any).AwsWafIntegration;
    return typeof integration?.getToken === 'function' && typeof integration?.forceRefreshToken === 'function';
  }
}
